import React, { Fragment, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Btn, H2, P } from '../AbstractElements';
import { EmailAddress, ResetPasswordTitle, NewPasswordLabel, ConfirmNewPasswordLabel, ResetPasswordButton, BackToLogin } from '../Constant';
import AuthShell from './AuthShell';
import API_BASE_URL from '../Config/api';

// Public page for /reset-password?token=...&email=... (from the emailed
// link). Also accepts a manually-entered reset code as an alternative to the
// link, per the reset email containing both.
const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const [email, setEmail] = useState(searchParams.get('email') || '');
    const [otp, setOtp] = useState('');
    const [useCodeInstead, setUseCodeInstead] = useState(!token);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        if (!email) {
            toast.error('Please enter your email address.');
            return;
        }
        if (useCodeInstead && !otp) {
            toast.error('Please enter the reset code from your email.');
            return;
        }
        if (!useCodeInstead && !token) {
            toast.error('This reset link is missing its token.');
            return;
        }
        if (newPassword.length < 8) {
            toast.error('Password must be at least 8 characters long.');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match.');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(`${API_BASE_URL}/api/auth/reset-password`, {
                email,
                token: useCodeInstead ? undefined : token,
                otp: useCodeInstead ? otp : undefined,
                newPassword,
            });
            toast.success('Password reset successfully!');
            setDone(true);
            setTimeout(() => navigate('/login'), 1500);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Could not reset your password. The link or code may have expired.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthShell>
            <Form className="theme-form">
                <H2 attrH2={{ className: 'text-center' }}>{ResetPasswordTitle}</H2>
                {done ? (
                    <P attrPara={{ className: 'text-center' }}>Your password has been reset. Redirecting you to login&hellip;</P>
                ) : (
                    <Fragment>
                        <FormGroup className='form-group'>
                            <Label className="col-form-label">{EmailAddress}</Label>
                            <Input className="form-control" type="email" value={email} onChange={e => setEmail(e.target.value)} />
                        </FormGroup>
                        {useCodeInstead && (
                            <FormGroup className='form-group'>
                                <Label className="col-form-label">Reset code</Label>
                                <Input
                                    className="form-control" type="text" inputMode="numeric" maxLength={6}
                                    value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                                />
                            </FormGroup>
                        )}
                        <FormGroup className='form-group'>
                            <Label className="col-form-label">{NewPasswordLabel}</Label>
                            <Input className="form-control" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                        </FormGroup>
                        <FormGroup className='form-group'>
                            <Label className="col-form-label">{ConfirmNewPasswordLabel}</Label>
                            <Input className="form-control" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
                        </FormGroup>
                        <div className='text-end mt-3'>
                            <Btn attrBtn={{ color: 'primary w-100', className: 'btn-block', disabled: submitting, onClick: submit }}>
                                {submitting ? 'Resetting...' : ResetPasswordButton}
                            </Btn>
                        </div>
                        {token && (
                            <div className='text-center mt-2'>
                                <Btn attrBtn={{ color: 'link', className: 'p-0', type: 'button', onClick: () => setUseCodeInstead(!useCodeInstead) }}>
                                    {useCodeInstead ? 'Use the emailed link instead' : 'Use the code from your email instead'}
                                </Btn>
                            </div>
                        )}
                    </Fragment>
                )}
                <div className='text-center mt-4'>
                    <Link className="link" to='/login'>{BackToLogin}</Link>
                </div>
            </Form>
        </AuthShell>
    );
};

export default ResetPassword;
